import React from 'react';

import { isBrowser, isMobileOnly, isTablet } from 'react-device-detect';
import moment from 'moment';
import { UAParser } from 'ua-parser-js';
import isPropValid from '@emotion/is-prop-valid';
import { SOURCES, SOURCES_LABEL } from '../constants/sources';
import { BASE_NAME_APP, FUNCTIONARY_GROUP_ID, SCHOOL_TEACHER_GROUP_ID, UNIVERSITY_TEACHER_GROUP_ID } from '../config';
import { B1_PRELIMINARY_TRAINING, CONCEPTUAL_LINK, CORE, EXPANSION, FAST_TRACK, FOUNDATION, GROWTH } from '../constants/phases';
import { PERFORMER } from '../constants/start-page';
import { domToReact } from 'html-react-parser';

export const parseDomain = () => {
    const domain = window.location.hostname;

    if (domain.includes('zte-universita')) {
        return SOURCES_LABEL.ZTE_UNIVERSITA_LABEL;
    } else if (domain.includes('zte-unitutor')) {
        return SOURCES_LABEL.ZTE_UNITUTOR_LABEL;
    } else if (domain.includes('zte')) {
        return SOURCES_LABEL.ZTE_SCUOLA_LABEL;
    } else {
        return 'TEST';
    }
};

export const getDeviceType = () => {
    if (isBrowser) {
        return 'DESKTOP';
    } else if (isMobileOnly) {
        return 'MOBILE';
    } else if (isTablet) {
        return 'TABLET';
    } else {
        return 'UNKNOWN';
    }
};

export const isAppInIframe = () => {
    try {
        return window.self !== window.top;
    } catch {
        return true; // handle cross-origin embedding
    }
};

export const zeroPad = (num, places) => String(num).padStart(places, '0');
export const printTime = (time) => {
    const minutes = Math.floor(time / 60);
    return `${zeroPad(minutes, 2)}:${zeroPad(time - minutes * 60, 2)}`;
};

export const getTimeString = (seconds) => {
    if (!seconds || seconds < 0) {
        return '00:00';
    }

    const minutesString = String(Math.floor(seconds / 60)).padStart(2, '0');
    const secondsString = String(Math.floor(seconds) % 60).padStart(2, '0');
    return `${minutesString}:${secondsString}`;
};

export const getBaseUrl = () => {
    const url = window.location.href.split('/');
    const protocol = url[0];
    const domain = url[2];

    return `${protocol}//${domain}`;
};

export const getRedirectToSource = (source, bookId, homeworkId, backToHome, ugc) => {
    switch (source) {
        case SOURCES.GOOGLE_CLASSROOM:
            return 'https://classroom.google.com';
        case SOURCES.TT:
            return `${getBaseUrl()}/matematicabergamini`;
        case SOURCES.LAZE:
            if (backToHome || !homeworkId) {
                return `${getBaseUrl()}`;
            }

            return `${getBaseUrl()}${!ugc ? `/prova/-/${homeworkId}` : `/prova-insegnante/-/${homeworkId}`}`;
        case SOURCES.ZTE_SCUOLA:
        case SOURCES.ZTE_UNIVERSITA:
        case SOURCES.ZTE_UNITUTOR:
            return `${getBaseUrl()}${bookId > 0 ? `/prove/${bookId}` : ''}`;
        default:
            return getBaseUrl();
    }
};

export const redirectToSourceHome = (source) => {
    let redirectTo = getBaseUrl();
    if (source && source === SOURCES.TT) {
        redirectTo += `/matematicabergamini`;
    }
    window.location.replace(redirectTo);
};

export const redirectToViewer = (payload) => {
    let redirectTo = getBaseUrl();

    window.location.replace(`${redirectTo}${BASE_NAME_APP}/?payload=${payload}`);
};

export const redirectToViewerCorrection = (payload) => {
    let redirectTo = getBaseUrl();
    window.location.replace(`${redirectTo}${BASE_NAME_APP}/correzione?payload=${payload}`);
};

export const modernBrowser = () => {
    const browser = new UAParser().getBrowser();
    return browser.name !== 'Edge' || parseInt(browser.major, 10) >= 80;
};

export const isChromeOnIos = () => {
    const uaParser = new UAParser();
    return uaParser.getBrowser().name === 'Chrome' && uaParser.getOS().name === 'iOS';
};

export const isFirefoxOnAndroid = () => {
    const uaParser = new UAParser();
    return uaParser.getBrowser().name === 'Firefox' && uaParser.getOS().name === 'Android';
};

export const textAssignmentEndDate = (assignmentEndDateString = Date.now()) => {
    const assignmentEndDate = new Date(assignmentEndDateString);
    const isToday = new Date().toDateString() === assignmentEndDate.toDateString();
    const endTime = new Intl.DateTimeFormat('it-IT', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: false,
    }).format(assignmentEndDate);
    const endDay = isToday ? 'oggi' : new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'numeric' }).format(assignmentEndDate);

    return `Puoi svolgere questa prova fino alle ${endTime} ${isToday ? 'di' : 'del'} ${endDay}`;
};

export const base64ToBlob = async (base64) => {
    const url = `data:application/zip;base64,${base64}`;
    try {
        const res = await fetch(url);
        return res.blob();
    } catch {
        return null;
    }
};

export const saveData = (data, name) => {
    const a = document.createElement('a');
    document.body.appendChild(a);
    a.style = 'display: none';

    const blob = new Blob(data, { type: 'octet/stream' });
    const url = window.URL.createObjectURL(blob);
    a.href = url;
    a.download = name;
    a.click();

    window.URL.revokeObjectURL(url);
    a.remove();
};

export const parseDate = (dateTime, format = 'DD/MM/YYYY HH:mm') => {
    const parsedDate = moment(dateTime);

    return parsedDate.isValid() ? parsedDate.format(format) : '';
};

export const parseTheories = (theories) => {
    const result = {};

    for (let i = 0; i < theories.length; i++) {
        const theory = theories[i];
        if (!theory.path) {
            continue;
        }

        const path = theory.path[0];
        const id = path.map((p) => p.id).join('_');

        const theoryCompound = {
            contentType: theory.contenttype,
            xml: theory.xmldescription,
            title: theory.publicTitle,
            context: { ...path[0] },
            argument: { ...path[1] },
            theme: { ...path[2] },
            microTheme: { ...path[3] },
        };

        if (result[id]) {
            result[id].push(theoryCompound);
        } else {
            result[id] = [theoryCompound];
        }
    }

    return result;
};

export const stripHTMLTags = (text) => (text ? text.replace(/<[^>]+>/g, '') : '');

export const setPageTitle = (source, homeworkPublicTitle) => {
    const getBaseTitle = () => {
        switch (source) {
            case SOURCES.TT:
                return 'Tutor matematica';
            case SOURCES.LAZE:
                return 'laZ Esercizi';
            default:
                return 'ZTE';
        }
    };

    document.title = `${getBaseTitle()}${homeworkPublicTitle ? ` - ${stripHTMLTags(homeworkPublicTitle)}` : ''}`;
};

export const getEndDate = (result) => {
    let endDate = null;

    if (result.dueDate) {
        let time = `23:59:00.000+0000`;
        const month = result.dueDate.month < 10 ? `0${result.dueDate.month}` : result.dueDate.month;
        const day = result.dueDate.day < 10 ? `0${result.dueDate.day}` : result.dueDate.day;
        const date = `${result.dueDate.year}-${month}-${day}T`;

        if (result.dueTime) {
            const hours = result.dueTime.hours < 10 ? `0${result.dueTime.hours}` : result.dueTime.hours;
            const minutes = result.dueTime.minutes
                ? result.dueTime.minutes < 10
                    ? `0${result.dueTime.minutes}`
                    : result.dueTime.minutes
                : `00`;

            time = `${hours}:${minutes}:00.000+0000`;
        }

        endDate = `${date}${time}`;
    }

    return endDate;
};

export const getSourceLabel = (source) => {
    switch (source) {
        case SOURCES.GOOGLE_CLASSROOM:
            return 'Google Classroom';
        case SOURCES.TT:
            return 'TUTOR';
        case SOURCES.LAZE:
            if (window.location.href.split('/')[2].includes('crea-esercizi')) {
                return 'laZ Crea Esercizi';
            } else {
                return 'laZ Esercizi';
            }
        default:
            return 'ZTE';
    }
};

export const isElementWidthInViewport = (elem) => {
    let left = elem.offsetLeft;
    let width = elem.offsetWidth;

    while (elem.offsetParent) {
        elem = elem.offsetParent;
        left += elem.offsetLeft;
    }
    return left >= window.pageXOffset && left + width <= window.pageXOffset + window.innerWidth;
};

//        if (location.pathname === START_PAGE && firstPage === PERFORMER) {

export const getPerformerBackgroundColor = (phase, firstPage) => {
    if (firstPage !== PERFORMER) {
        return '';
    }

    if (phase?.startsWith('Conceptual Link')) {
        return CONCEPTUAL_LINK.backgroundColor;
    }

    switch (phase) {
        case FOUNDATION.name:
            return FOUNDATION.backgroundColor;
        case CORE.name:
            return CORE.backgroundColor;
        case EXPANSION.name:
            return EXPANSION.backgroundColor;
        case GROWTH.name:
            return GROWTH.backgroundColor;
        case B1_PRELIMINARY_TRAINING.name:
            return B1_PRELIMINARY_TRAINING.backgroundColor;
        case FAST_TRACK.name:
            return FAST_TRACK.backgroundColor;
        default:
            return '';
    }
};

export const getPerformerBoxColor = (phase) => {
    if (phase && phase.startsWith('Conceptual Link')) {
        return CONCEPTUAL_LINK.boxColor;
    }
    switch (phase) {
        case FOUNDATION.name:
            return FOUNDATION.boxColor;
        case CORE.name:
            return CORE.boxColor;
        case EXPANSION.name:
            return EXPANSION.boxColor;
        case GROWTH.name:
            return GROWTH.boxColor;
        case B1_PRELIMINARY_TRAINING.name:
            return B1_PRELIMINARY_TRAINING.boxColor;
        case FAST_TRACK.name:
            return FAST_TRACK.boxColor;
        default:
            return '';
    }
};

export const getPerformerColor = (phase) => {
    if (phase?.startsWith('Conceptual Link')) {
        return CONCEPTUAL_LINK.color;
    }
    switch (phase) {
        case FOUNDATION.name:
            return FOUNDATION.color;
        case CORE.name:
            return CORE.color;
        case EXPANSION.name:
            return EXPANSION.color;
        case GROWTH.name:
            return GROWTH.color;
        case B1_PRELIMINARY_TRAINING.name:
            return B1_PRELIMINARY_TRAINING.color;
        case FAST_TRACK.name:
            return FAST_TRACK.color;
        default:
            return '';
    }
};

export const parseMfenced = (domNode, options) => {
    const { open, close } = domNode.attribs;

    const openChar = open === undefined ? '(' : open === '' ? null : open;
    const closeChar = close === undefined ? ')' : close === '' ? null : close;

    return (
        <mrow>
            {openChar && <mo fence="true">{openChar}</mo>}
            {domToReact(domNode.children, options)}
            {closeChar && <mo fence="true">{closeChar}</mo>}
        </mrow>
    );
};

export const isTeacher = (groupId) =>
    SCHOOL_TEACHER_GROUP_ID === groupId || UNIVERSITY_TEACHER_GROUP_ID === groupId || FUNCTIONARY_GROUP_ID === groupId;

// This implements the default behavior from styled-components v5
export const shouldForwardProp = (propName, target) => {
    if (typeof target === 'string') {
        // For HTML elements, forward the prop if it is a valid HTML attribute
        return isPropValid(propName);
    }
    // For other elements, forward all props
    return true;
};

export const indexToLetter = (index) => {
    return String.fromCharCode(65 + index);
};
